import { useEffect, useMemo, useState } from 'react';
import { money, useLoad } from '../lib';
import { records, type AclResource, type Body, type Category, type PermissionTemplate, type RecordOptions } from '../records-api';
import { CategoryPicker, ChangesCard, LIMIT_LABEL, PACKAGE_LABEL, SaveBar, SystemOfRecordNote, errText, type Outcome } from '../records';
import type { OpsChange } from '../api';

// Permission templates — Adobe's buyer templates (Customers › Manage Template): a permission tree and
// four sub-account settings, copied onto a camp's people from the camp's People list (panels/Camps.tsx).
// List, create, edit and deactivate here. Reads for all staff; edits for admin/ops.

const FILTERS = [['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']] as const;

/** How a template orders, from the three permissions the rebuild acts on (commerce shared/src/rules/permissions.ts). */
export function orderingOf(resources: string[]): string {
  if (resources.includes('Magento_Sales::place_order')) return 'Places orders';
  if (resources.includes('Intellam_Sales::order_approval_needed')) return 'Needs approval';
  return 'Cannot order';
}
export const approvesCarts = (resources: string[]) => resources.includes('Intellam_RolesAndPermissions::can_approve_shopping_carts');
export const limitText = (t: Pick<PermissionTemplate, 'order_limit_period' | 'order_limit_value'>) =>
  (t.order_limit_period ? `${money(Number(t.order_limit_value ?? 0))} ${LIMIT_LABEL[t.order_limit_period] ?? ''}` : 'Not limited');

export function PermissionTemplatesPanel({ canEdit }: { canEdit: boolean }) {
  const [filter, setFilter] = useState<string>('active');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<number | 'new' | null>(null);
  const { data, err, reload } = useLoad(() => records.templates(), []);

  if (open != null && data) {
    return <TemplateEditor id={open} resources={data.resources} canEdit={canEdit}
      onBack={() => { setOpen(null); reload(); }} onCreated={(id) => setOpen(id)} />;
  }
  const rows = (data?.templates ?? []).filter((t) => (filter === 'all' || t.is_active === (filter === 'active'))
    && (!q.trim() || t.name.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <div className="stack">
      <h1>Permission templates</h1>
      <p className="sub">Reusable buyer permission sets, as in Adobe's Customers › Manage Template. Apply one from a camp's People list.</p>
      <div className="toolbar">
        <input className="search" placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} />
        {FILTERS.map(([v, l]) => <button key={v} className={`chip ${filter === v ? 'on' : ''}`} onClick={() => setFilter(v)}>{l}</button>)}
        {canEdit && <button className="btn-sm" disabled={!data} onClick={() => setOpen('new')}>New template</button>}
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card mr-scroll">
        <table>
          <thead><tr><th>Template</th><th>Ordering</th><th>Approves carts</th><th>Order limit</th><th>Package email</th><th className="num">Blocked categories</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className={`row-click ${t.is_active ? '' : 'inactive'}`} onClick={() => setOpen(t.id)}>
                <td><strong>{t.name}</strong>{t.magento_template_id != null && <div className="muted small">Adobe template {t.magento_template_id}</div>}</td>
                <td>{orderingOf(t.allowed_resources)}</td>
                <td>{approvesCarts(t.allowed_resources) ? 'Yes' : '—'}</td>
                <td>{limitText(t)}</td>
                <td className="small">{t.package_email ? PACKAGE_LABEL[t.package_email] ?? t.package_email : '—'}</td>
                <td className="num">{t.blocked_category_ids.length || '—'}</td>
                <td>{t.is_active ? <span className="pill edi">active</span> : <span className="pill">inactive</span>}</td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={7} className="loading">No templates match.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

interface Draft { name: string; period: string; limit: string; pkg: string; blocks: number[]; allowed: string[] }
const draftOf = (t: PermissionTemplate | null): Draft => ({
  name: t?.name ?? '', period: t?.order_limit_period ?? '', limit: t?.order_limit_value == null ? '' : String(Number(t.order_limit_value)),
  pkg: t?.package_email ?? 'none', blocks: t?.blocked_category_ids ?? [], allowed: t?.allowed_resources ?? ['Magento_Company::index'],
});
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** The body a save sends: everything for a new template, only what changed for an existing one. */
function bodyOf(d: Draft, init: Draft, isNew: boolean): Body {
  const b: Body = {};
  if (isNew || d.name !== init.name) b.name = d.name;
  if (isNew || d.period !== init.period) b.orderLimitPeriod = d.period || null;
  if (isNew || d.limit !== init.limit) b.orderLimitValue = d.limit.trim() || null;
  if (isNew || d.pkg !== init.pkg) b.packageEmail = d.pkg || null;
  if (isNew || JSON.stringify(d.blocks) !== JSON.stringify(init.blocks)) b.categoryIds = d.blocks;
  if (isNew || !sameSet(d.allowed, init.allowed)) b.allowedResources = d.allowed;
  return b;
}

function TemplateEditor({ id, resources, canEdit, onBack, onCreated }: {
  id: number | 'new'; resources: AclResource[]; canEdit: boolean; onBack: () => void; onCreated: (id: number) => void;
}) {
  const isNew = id === 'new';
  const [t, setT] = useState<PermissionTemplate | null>(null);
  const [changes, setChanges] = useState<OpsChange[]>([]);
  const [opts, setOpts] = useState<RecordOptions | null>(null);
  const [cats, setCats] = useState<Category[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<Outcome | null>(null);
  const init = useMemo(() => draftOf(t), [t]);
  const [d, setD] = useState<Draft>(init);
  useEffect(() => setD(init), [init]);

  useEffect(() => {
    if (!isNew) records.template(id).then((r) => { setT(r.template); setChanges(r.changes); }).catch((e) => setErr(errText(e)));
    records.options().then(setOpts).catch((e) => setErr(errText(e)));
    records.categories().then(setCats).catch(() => setCats([]));
  }, [id]);

  async function save(body: Body, what: string) {
    setBusy(true); setOut(null);
    try {
      if (isNew) {
        const r = await records.createTemplate(body);
        onCreated(r.template.id);
        return;
      }
      const r = await records.saveTemplate(id, body);
      setT(r.template); setChanges(r.changes);
      setOut({ tag: 'save', bad: false, text: r.changed.length === 0 ? 'Nothing changed.' : `Saved ${what}.` });
    } catch (e) {
      setOut({ tag: 'save', bad: true, text: errText(e) });
    } finally { setBusy(false); }
  }

  const back = <div><button className="btn-sm ghost" onClick={onBack}>← Back to templates</button></div>;
  if ((!isNew && !t) || !opts) return <div className="stack">{back}{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  const body = bodyOf(d, init, isNew);
  const dirty = isNew ? d.name.trim() !== '' : Object.keys(body).length > 0;
  const disabled = !canEdit;
  const adobeOnly = resources.filter((r) => !r.railway).length;

  return (
    <div className="stack">
      {back}
      <div className="mr-head">
        <h1>{isNew ? 'New template' : t!.name}</h1>
        {t && (t.is_active ? <span className="pill edi">active</span> : <span className="pill">inactive</span>)}
        {canEdit && t && (
          <button className="btn-sm ghost" disabled={busy} onClick={() => {
            if (t.is_active && !window.confirm(`Deactivate "${t.name}"? It can no longer be applied; people it was applied to keep their permissions.`)) return;
            save({ isActive: !t.is_active }, t.is_active ? 'the template as inactive' : 'the template as active');
          }}>{t.is_active ? 'Deactivate' : 'Reactivate'}</button>
        )}
      </div>
      {t && <p className="sub">{t.magento_template_id != null ? `Adobe template ${t.magento_template_id} · ` : 'Made here · '}updated {t.updated_at.slice(0, 10)}{t.updated_by ? ` by ${t.updated_by}` : ''}</p>}
      <SystemOfRecordNote />
      {err && <p className="msg bad">⚠ {err}</p>}

      <section className="card">
        <h2>Template</h2>
        <p className="muted mr-lead">Applying copies these onto the person: their order limit, package email and blocked categories are replaced with the template's.</p>
        <div className="tf-grid">
          <label className="mr-span">Name
            <input value={d.name} disabled={disabled} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="e.g. FOODSERVICE (new 1.2021)" />
          </label>
          <label>Order limit
            <select value={d.period} disabled={disabled} onChange={(e) => setD({ ...d, period: e.target.value })}>
              <option value="">Not limited</option>{opts.limitPeriods.map((p) => <option key={p} value={p}>{LIMIT_LABEL[p] ?? p}</option>)}
            </select>
          </label>
          <label>Limit amount ($)
            <input inputMode="decimal" value={d.limit} disabled={disabled} onChange={(e) => setD({ ...d, limit: e.target.value })} placeholder="—" />
          </label>
          <label>Package email
            <select value={d.pkg} disabled={disabled} onChange={(e) => setD({ ...d, pkg: e.target.value })}>
              {opts.packageEmail.map((p) => <option key={p} value={p}>{PACKAGE_LABEL[p] ?? p}</option>)}
            </select>
          </label>
        </div>
        <div className="iw-lbl pt-gap">Blocked categories</div>
        <CategoryPicker categories={cats} ids={d.blocks} onChange={(blocks) => setD({ ...d, blocks })} disabled={disabled} />
      </section>

      <section className="card">
        <h2>Permissions</h2>
        <p className="muted mr-lead">
          Adobe's permission tree. Applying a template writes the {resources.length - adobeOnly} marked with a Railway column to the
          person; the other {adobeOnly} have no Railway equivalent yet — the template keeps them, and they still take effect only in Adobe.
        </p>
        <PermissionTree resources={resources} allowed={d.allowed} disabled={disabled} onChange={(allowed) => setD({ ...d, allowed })} />
      </section>

      {canEdit && (
        <section className="card">
          <SaveBar dirty={dirty} busy={busy} outcome={out} onReset={isNew ? undefined : () => setD(init)}
            onSave={() => save(body, 'the template')} hint={isNew ? 'Name it, then save.' : 'Editing a template changes nobody it was already applied to.'} />
        </section>
      )}
      <ChangesCard changes={changes} />
    </div>
  );
}

/**
 * The tree as nested checkboxes. Allowing a permission allows its parents (Magento checks a parent
 * before its child); taking one away takes away everything under it.
 */
function PermissionTree({ resources, allowed, disabled, onChange }: {
  resources: AclResource[]; allowed: string[]; disabled: boolean; onChange: (next: string[]) => void;
}) {
  const kids = useMemo(() => {
    const m = new Map<string | null, AclResource[]>();
    for (const r of resources) m.set(r.parent, [...(m.get(r.parent) ?? []), r]);
    return m;
  }, [resources]);
  const byId = useMemo(() => new Map(resources.map((r) => [r.id, r])), [resources]);
  const below = (id: string): string[] => (kids.get(id) ?? []).flatMap((k) => [k.id, ...below(k.id)]);
  const above = (id: string): string[] => { const p = byId.get(id)?.parent; return p ? [p, ...above(p)] : []; };
  const toggle = (id: string, on: boolean) => {
    const next = on ? new Set([...allowed, id, ...above(id)]) : new Set(allowed.filter((x) => x !== id && !below(id).includes(x)));
    onChange(resources.map((r) => r.id).filter((x) => next.has(x)));
  };
  const branch = (parent: string | null) => (
    <ul className="pt-tree">
      {(kids.get(parent) ?? []).map((r) => (
        <li key={r.id}>
          <label className="chk">
            <input type="checkbox" checked={allowed.includes(r.id)} disabled={disabled || r.parent == null} onChange={(e) => toggle(r.id, e.target.checked)} />
            {r.label}
            {r.railway ? <span className="pill info pt-col">Railway: {r.railway}</span> : kids.has(r.id) ? null : <span className="muted small">Adobe only</span>}
          </label>
          {kids.has(r.id) && branch(r.id)}
        </li>
      ))}
    </ul>
  );
  return <div className="mr-scroll">{branch(null)}</div>;
}
